/**
 * CountrySelector — Wave 45 global marketplace filter chip group.
 *
 * Pattern: URL ?retailer=<code> primary + commerco_active_marketplace cookie
 * fallback. router.replace() preserves searchParams via cloning the existing
 * URLSearchParams; cookie write is idempotent (30-day persistence).
 *
 * Reads the active retailer from useSearchParams() so the chip stays in sync
 * with server-rendered URLs. ArrowLeft / ArrowRight cycles through options;
 * Home / End jump to first / last.
 */
"use client";

import * as React from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";

import { cn } from "@commerco/mercato";

import {
  MARKETPLACE_COOKIE,
  RETAILER_OPTIONS,
  VALID_RETAILERS,
  type RetailerCode,
} from "@/lib/marketplace-filter-constants";

function readClientCookie(): RetailerCode | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${MARKETPLACE_COOKIE}=([^;]+)`),
  );
  if (!match) return null;
  const raw = decodeURIComponent(match[1] ?? "");
  return (VALID_RETAILERS as readonly string[]).includes(raw)
    ? (raw as RetailerCode)
    : null;
}

function writeClientCookie(code: RetailerCode | null): void {
  const maxAge = 60 * 60 * 24 * 30;
  if (code) {
    document.cookie =
      `${MARKETPLACE_COOKIE}=${encodeURIComponent(code)};` +
      ` Max-Age=${maxAge}; Path=/; SameSite=Lax`;
  } else {
    document.cookie = `${MARKETPLACE_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
  }
}

export function CountrySelector(): React.JSX.Element {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const urlActive = searchParams.get("retailer");
  const [active, setActive] = React.useState<RetailerCode | null>(() => {
    if (urlActive && (VALID_RETAILERS as readonly string[]).includes(urlActive)) {
      return urlActive as RetailerCode;
    }
    return null;
  });

  React.useEffect(() => {
    if (urlActive && (VALID_RETAILERS as readonly string[]).includes(urlActive)) {
      setActive(urlActive as RetailerCode);
    } else if (!urlActive) {
      const cookieValue = readClientCookie();
      setActive(cookieValue);
    }
  }, [urlActive]);

  const switchTo = React.useCallback(
    (code: RetailerCode | null) => {
      writeClientCookie(code);
      setActive(code);
      const params = new URLSearchParams(searchParams.toString());
      if (code) {
        params.set("retailer", code);
      } else {
        params.delete("retailer");
      }
      const query = params.toString();
      router.replace(`${pathname}${query ? `?${query}` : ""}`, { scroll: false });
      // W52.3 — server components don't re-fetch on URL-only changes; refresh
      // forces RSC re-render so dashboard widgets pick up the new retailer scope.
      router.refresh();
    },
    [router, pathname, searchParams],
  );

  const onKey = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const idx = RETAILER_OPTIONS.findIndex((o) => o.code === active);
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const next = (idx + 1) % RETAILER_OPTIONS.length;
        switchTo(RETAILER_OPTIONS[next]!.code);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prev = (idx - 1 + RETAILER_OPTIONS.length) % RETAILER_OPTIONS.length;
        switchTo(RETAILER_OPTIONS[prev]!.code);
      } else if (e.key === "Home") {
        e.preventDefault();
        switchTo(RETAILER_OPTIONS[0]!.code);
      } else if (e.key === "End") {
        e.preventDefault();
        switchTo(RETAILER_OPTIONS[RETAILER_OPTIONS.length - 1]!.code);
      }
    },
    [active, switchTo],
  );

  return (
    <div
      role="radiogroup"
      aria-label="Pazaryeri filtresi"
      onKeyDown={onKey}
      data-component="country-selector"
      className="flex items-center gap-1.5"
    >
      {RETAILER_OPTIONS.map((opt) => {
        const checked = active === opt.code;
        return (
          <button
            key={opt.code ?? "all"}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={opt.aria}
            tabIndex={checked ? 0 : -1}
            onClick={() => switchTo(opt.code)}
            data-retailer={opt.code ?? "all"}
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-all"
            style={
              checked
                ? {
                    background: "linear-gradient(135deg, oklch(0.62 0.16 195), oklch(0.46 0.14 195))",
                    borderColor: "transparent",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 0 0 2px oklch(0.62 0.16 195 / 0.20)",
                  }
                : {
                    background: "var(--color-bg-surface)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-fg-secondary)",
                  }
            }
          >
            <span aria-hidden="true" className="text-[14px] leading-none">
              {opt.flag}
            </span>
            <span className="hidden sm:inline">{opt.label}</span>
            {checked ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className="ml-0.5">
                <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
